<script setup>
import { useSharedIntersectionObserver } from "@/composables/useSharedIntersectionObserver";

const props = defineProps({
    isBlack: {
        type: Boolean,
        default: false,
    },
    speed: {
        type: Number,
        default: 1,
    },
    // 是否啟用懶載入（進入視窗才載入動畫資料）
    lazyLoad: {
        type: Boolean,
        default: true,
    },
});

const ballRef = ref(null);
const containerRef = ref(null);
const animationData = ref(null);
const isLoaded = ref(false);

// 使用共享 Observer（懶載入用）
const { observe, unobserve } = useSharedIntersectionObserver({
    threshold: 0,
    rootMargin: "100px", // 提前 100px 開始載入
});

// 動態載入 Lottie JSON 資料
const loadAnimationData = async () => {
    if (isLoaded.value) return;
    
    try {
        if (props.isBlack) {
            const module = await import("@/assets/lottie/gama_globe_black.json");
            animationData.value = module.default;
        } else {
            const module = await import("@/assets/lottie/gama_globe_orange.json");
            animationData.value = module.default;
        }
        isLoaded.value = true;
    } catch (error) {
        console.error("Failed to load Lottie animation:", error);
    }
};

onMounted(() => {
    if (!props.lazyLoad) {
        // 不使用懶載入，立即載入
        loadAnimationData();
    } else if (containerRef.value) {
        // 使用共享 Intersection Observer 懶載入
        observe(containerRef.value, (entry) => {
            if (entry.isIntersecting && !isLoaded.value) {
                loadAnimationData();
                unobserve(containerRef.value);
            }
        });
    }
});

onBeforeUnmount(() => {
    unobserve(containerRef.value);
});

defineExpose({
    ballRef,
});
</script>

<template>
    <div ref="containerRef" class="h-full w-full">
        <ClientOnly>
            <LottieAnimation
                v-if="animationData"
                :animationData="animationData"
                ref="ballRef"
                class="h-full w-full"
                :speed="speed"
            />
        </ClientOnly>
    </div>
</template>
